import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Empty, Spin, Typography, Space, Divider, Grid, message } from 'antd';
import {
  GoogleOutlined, LogoutOutlined, ReloadOutlined, DeleteOutlined, HeartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ProductImage from '../components/ProductImage';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

export default function WishlistPage() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);

  // 從網址參數處理登入導回（帶 ctoken），並取得客人資訊
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ctoken = q.get('ctoken');
    if (ctoken) {
      try { localStorage.setItem('customer_token', ctoken); } catch {}
      q.delete('ctoken');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    const token = ctoken || getToken();
    if (!token) { setCheckingAuth(false); return; }
    fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) setCustomer(info);
        else { try { localStorage.removeItem('customer_token'); } catch {} }
      })
      .catch(() => {})
      .finally(() => setCheckingAuth(false));
  }, []);

  // 載入願望清單
  const loadWishlist = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (customer) loadWishlist();
  }, [customer, loadWishlist]);

  const startCustomerLogin = useCallback(() => {
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent('/wishlist')}`;
  }, []);

  const logoutCustomer = useCallback(() => {
    try { localStorage.removeItem('customer_token'); } catch {}
    setCustomer(null);
    setItems(null);
  }, []);

  const removeItem = useCallback((productId) => {
    const token = getToken();
    if (!token) return;
    setRemoving(productId);
    fetch(`${API_BASE}/api/wishlist/${productId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('remove failed');
        setItems((prev) => (prev || []).filter((it) => it.product_id !== productId));
      })
      .catch(() => message.error('移除失敗，請稍後再試'))
      .finally(() => setRemoving(null));
  }, []);

  const containerStyle = {
    maxWidth: isMobile ? 600 : 1160,
    margin: '0 auto',
    padding: isMobile ? '16px' : '28px 32px 48px',
    position: 'relative',
    zIndex: 1,
  };

  // 尚在驗證登入
  if (checkingAuth) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 未登入：顯示登入提示
  if (!customer) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 24 }}>
          <Title level={3} style={{ color: '#222A5A', fontWeight: 800 }}>願望清單</Title>
          <Text type="secondary">用 Google 登入後，即可查看您收藏的商品</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary" size="large" icon={<GoogleOutlined />}
            onClick={startCustomerLogin}
            style={{ height: 48, fontSize: 16 }}
          >
            登入查看願望清單
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style>{`
        .wishlist-card { transition: transform .18s ease, box-shadow .18s ease; }
        @media (hover: hover) { .wishlist-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(34,41,87,0.16); } }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0, color: BRAND, fontWeight: 800, letterSpacing: 1 }}>願望清單</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadWishlist} loading={loading}>重新整理</Button>
        </Space>
      </div>
      <Divider style={{ margin: '12px 0' }} />

      {loading && items === null ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
        </div>
      ) : !(items && items.length > 0) ? (
        <Empty
          image={<HeartOutlined style={{ fontSize: 48, color: 'var(--brand-primary)' }} />}
          description="還沒有收藏任何商品"
          style={{ padding: '48px 0' }}
        >
          <Button type="primary" href="/shop">去逛逛商品</Button>
        </Empty>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: isMobile ? 12 : 22,
            alignItems: 'start',
          }}
        >
        {items.map((it) => (
          <Card
            key={it.product_id}
            className="wishlist-card"
            onClick={() => navigate(`/product/${it.product_id}`)}
            style={{ borderRadius: 16, borderColor: BORDER, cursor: 'pointer', overflow: 'hidden' }}
            bodyStyle={{ padding: 0 }}
            cover={<ProductImage src={it.image_url} alt={it.name} />}
          >
            <div style={{ padding: 12 }}>
              <Text
                strong
                style={{ color: HEADING, display: 'block' }}
                ellipsis={{ tooltip: it.name }}
              >
                {it.name}
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 }}>
                <Text strong style={{ color: BRAND, fontSize: 16 }}>
                  {it.price != null ? `NT$ ${it.price}` : '—'}
                </Text>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={removing === it.product_id}
                  onClick={(e) => { e.stopPropagation(); removeItem(it.product_id); }}
                >
                  移除
                </Button>
              </div>
            </div>
          </Card>
        ))}
        </div>
      )}
    </div>
  );
}
